import React, { useMemo, useCallback, useState } from 'react'
import usePerformEventToTask from 'pages/Tasks/hooks/usePerformEventToTask'
import { EventsOfTask } from 'pages/Tasks/types'
import { DIALOG_SIZES } from 'redesign/components/molecules/dialog/constants'
import AcceptMultipleBody from '../buttons/accept-multiple-monthly-sheets/accept-multiple-body'
import AcceptMultipleFooter from '../buttons/accept-multiple-monthly-sheets/accept-multiple-footer'
import { LOADING_FIRST_QUERY, LOADING_SECOND_QUERY } from '../constants'
import type { UseAcceptMultipleMonthsT } from '../types'

export const useAcceptMultipleMonths = ({
  prevMonthTask,
  setDialogProps,
  refetch,
  sendEvent,
  eventLoading,
  t,
}: UseAcceptMultipleMonthsT) => {
  const [isAcceptingBoth, setIsAcceptingBoth] = useState(false)
  const { loading: loadingPrevious, sendEvent: sendEventPrevious } = usePerformEventToTask(
    prevMonthTask?.favurUuid ?? '',
  )
  const acceptCurrentLabel = t('monthlySheet.acceptMultipleWtcDrawer.acceptCurrent')

  const onAction = useCallback(() => {
    setDialogProps(undefined)
    refetch()
  }, [refetch, setDialogProps])

  const acceptCurrent = useCallback(() => {
    sendEvent(EventsOfTask.monthlySheetAccept, undefined, onAction)
  }, [onAction, sendEvent])
  const acceptBoth = useCallback(() => {
    setIsAcceptingBoth(true)
    sendEventPrevious(EventsOfTask.monthlySheetAccept, undefined, () => {
      setDialogProps((prev) => ({
        ...prev,
        footer: (
          <AcceptMultipleFooter
            acceptCurrentLabel={acceptCurrentLabel}
            acceptBothLabel={t('monthlySheet.acceptMultipleWtcDrawer.acceptingMultiple', { num: LOADING_SECOND_QUERY })}
          />
        ),
      }))
      acceptCurrent()
    })
    setDialogProps((prev) => ({
      ...prev,
      footer: (
        <AcceptMultipleFooter
          acceptCurrentLabel={acceptCurrentLabel}
          acceptBothLabel={t('monthlySheet.acceptMultipleWtcDrawer.acceptingMultiple', { num: LOADING_FIRST_QUERY })}
        />
      ),
    }))
  }, [acceptCurrent, acceptCurrentLabel, sendEventPrevious, setDialogProps, t])

  const acceptBothLabel = useMemo(() => {
    if (!isAcceptingBoth || (!eventLoading && !loadingPrevious)) {
      return t('monthlySheet.acceptMultipleWtcDrawer.acceptBoth')
    }

    return t('monthlySheet.acceptMultipleWtcDrawer.acceptingMultiple', { num: loadingPrevious ? 1 : 2 })
  }, [eventLoading, isAcceptingBoth, loadingPrevious, t])

  const handleAcceptMultiple = () => {
    setDialogProps({
      //TODO: Update with correct label, using one that is close enough for now
      title: t('monthlySheet.acceptMultipleWtcDrawer.accepting'),
      footer: (
        <AcceptMultipleFooter
          acceptCurrentLabel={acceptCurrentLabel}
          acceptBothLabel={acceptBothLabel}
          onAcceptCurrentClick={acceptCurrent}
          onAcceptBothClick={acceptBoth}
        />
      ),
      content: <AcceptMultipleBody t={t} />,
      dialogSize: DIALOG_SIZES.medium,
    })
  }

  return { handleAcceptMultiple }
}
